import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SectionHeading from '@/components/brand/SectionHeading';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, X, TrendingUp } from 'lucide-react';
import { PrimaryButton } from '@/components/brand/Buttons';
import { HOME } from '@/constants/testIds';

const EASE = [0.22, 1, 0.36, 1];

const DEFAULT_STUDIES = [
  {
    brand: 'PropTech Platform',
    industry: 'Real Estate',
    title: 'AI Lead Qualification Transforms Property Sales Pipeline',
    summary:
      'Deployed an ML scoring engine that ranked inbound leads in real-time, cutting sales response time by half and tripling conversion rates across the entire funnel.',
    img: '/images/case-studies/fallback/photo-1486406146926-c627a92ad1ab.jpg',
    metric: '+38%',
    metricLabel: 'Qualified Leads',
  },
  {
    brand: 'Stari Hospitality',
    industry: 'Travel & Hospitality',
    title: 'Generative AI Concierge Redefines the Guest Experience',
    summary:
      'Built a 24/7 AI booking assistant handling 80% of guest queries autonomously across 12 languages — matching human-level satisfaction scores at a fraction of the cost.',
    img: '/images/case-studies/fallback/photo-1551882547-ff40c63fe5fa.jpg',
    metric: '−42%',
    metricLabel: 'Support Tickets',
  },
  {
    brand: 'Avon Manufacturing',
    industry: 'Industrial IoT',
    title: 'Computer Vision Achieves Near-Perfect Quality Control',
    summary:
      'Integrated edge-AI cameras with the production line to detect micro-defects invisible to the human eye — running at 3× the line speed with zero downtime.',
    img: '/images/case-studies/fallback/photo-1565728744382-61accd4aa148.jpg',
    metric: '99.2%',
    metricLabel: 'Defect Detection',
  },
];

const DEFAULT_HEADING = 'Case Studies';
const DEFAULT_EYEBROW  = 'Real Impact, Real Results';
const DEFAULT_SUBHEAD  =
  'Deep-dive into how Max Global Software solves complex challenges and delivers measurable outcomes for businesses worldwide.';

export default function CaseStudy({ data, items }) {
  const heading = data?.heading  || DEFAULT_HEADING;
  const eyebrow = data?.eyebrow  || DEFAULT_EYEBROW;
  const subhead  = data?.subhead  || DEFAULT_SUBHEAD;
  const studies  = Array.isArray(items) && items.length ? items : DEFAULT_STUDIES;

  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  return (
    <section
      id="case-study"
      className="py-16 relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #2F6FB0 0%, #537EA8 60%, #E8C25A 100%)' }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">

        <SectionHeading
          title={heading}
          subtitle={subhead}
          align="center"
          light
          className="mb-14"
        />

        {/* ── Cards ── */}
        <div
          className="grid lg:grid-cols-3 gap-6"
          data-testid={HOME.caseStudyCarousel}
        >
          {studies.map((s, idx) => (
            <CaseCard
              key={s.id || s.brand || idx}
              study={s}
              idx={idx}
              onOpen={() => setSelected(s)}
            />
          ))}
        </div>

        {/* ── Real Impact tagline — below cards, before CTA ── */}
        <motion.div
          className="mt-12 flex items-center gap-4"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.25, duration: 0.5, ease: EASE }}
        >
          <div className="flex-1 h-px bg-white/25" />
          <span className="text-white/85 text-sm font-semibold tracking-[0.18em] uppercase whitespace-nowrap">
            {eyebrow}
          </span>
          <div className="flex-1 h-px bg-white/25" />
        </motion.div>

        {/* ── CTA ── */}
        <motion.div
          className="mt-8 flex justify-center"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.38, duration: 0.6, ease: EASE }}
        >
          <PrimaryButton
            icon={<ArrowRight className="h-4 w-4" />}
            onClick={() => navigate('/case-studies')}
          >
            View All Case Studies
          </PrimaryButton>
        </motion.div>
      </div>

      {/* ── Popup modal ── */}
      <AnimatePresence>
        {selected && (
          <StudyModal
            study={selected}
            onClose={() => setSelected(null)}
            navigate={navigate}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Card
───────────────────────────────────────────── */
function CaseCard({ study, idx, onOpen }) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.article
      initial={{ opacity: 0, y: 44 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay: idx * 0.13, ease: EASE }}
      animate={{
        y: hovered ? -12 : 0,
        boxShadow: hovered
          ? '0 32px 64px rgba(31,45,69,0.28), 0 0 0 2px rgba(201,150,46,0.45)'
          : '0 8px 32px rgba(31,45,69,0.14)',
      }}
      style={{ boxShadow: '0 8px 32px rgba(31,45,69,0.14)' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onOpen}
      className="rounded-2xl bg-white overflow-hidden cursor-pointer border border-white/30"
    >
      {/* ── Image ── */}
      <div className="relative h-52 overflow-hidden">
        <motion.img
          src={study.img}
          alt={study.title}
          className="w-full h-full object-cover"
          style={{ scale: 1 }}
          animate={{ scale: hovered ? 1.08 : 1 }}
          transition={{ duration: 0.6, ease: EASE }}
          draggable={false}
        />

        {/* Hover overlay */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          animate={{ opacity: hovered ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          style={{ background: 'rgba(15,26,56,0.52)', opacity: 0 }}
        >
          <motion.span
            className="inline-flex items-center gap-1.5 text-[#08111F] text-xs font-bold px-4 py-2 rounded-full"
            style={{ background: 'linear-gradient(135deg,#C9962E,#E8C25A)' }}
            animate={{ y: hovered ? 0 : 10, opacity: hovered ? 1 : 0 }}
            transition={{ duration: 0.28, delay: 0.06, ease: EASE }}
          >
            View Details <ArrowUpRight className="h-3 w-3" />
          </motion.span>
        </motion.div>

        {/* Industry badge */}
        <span
          className="absolute top-3 left-3 text-[11px] font-semibold px-2.5 py-1 rounded-full"
          style={{ background: 'rgba(201,150,46,0.92)', color: '#08111F' }}
        >
          {study.industry || 'Technology'}
        </span>
      </div>

      {/* ── Body ── */}
      <div className="p-5">
        {/* Brand */}
        <p className="text-[11px] font-extrabold uppercase tracking-widest text-brandblue mb-2">
          {study.brand}
        </p>

        {/* Title */}
        <h3
          className="text-sm font-bold leading-snug mb-4 transition-colors duration-300"
          style={{ color: hovered ? '#2F6FB0' : '#1F2D45' }}
        >
          {study.title}
        </h3>

        {/* Metric row */}
        <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
          <TrendingUp
            className="h-4 w-4 flex-shrink-0"
            style={{ color: '#C9962E' }}
          />
          <span
            className="text-xl font-black leading-none"
            style={{
              background: 'linear-gradient(135deg,#C9962E,#E8C25A)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {study.metric}
          </span>
          {(study.metricLabel || study.metric_label) && (
            <span className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">
              {study.metricLabel || study.metric_label}
            </span>
          )}
        </div>
      </div>
    </motion.article>
  );
}

/* ─────────────────────────────────────────────
   Popup Modal
───────────────────────────────────────────── */
function StudyModal({ study, onClose, navigate }) {
  const handleViewMore = () => {
    onClose();
    navigate(study.slug ? `/case-studies/${study.slug}` : '/case-studies');
  };

  return (
    <>
      {/* Backdrop */}
      <motion.div
        className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.22 }}
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <motion.div
          className="relative w-full max-w-md bg-white rounded-3xl overflow-hidden shadow-2xl pointer-events-auto"
          initial={{ opacity: 0, scale: 0.86, y: 36 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.86, y: 36 }}
          transition={{ duration: 0.38, ease: EASE }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Hero image */}
          <div className="relative h-48 overflow-hidden">
            <img
              src={study.img}
              alt={study.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/65 to-transparent" />

            {/* Close button */}
            <button
              onClick={onClose}
              aria-label="Close"
              className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Bottom of image: industry + brand */}
            <div className="absolute bottom-4 left-5 right-5 flex items-center justify-between gap-2">
              <span
                className="text-[11px] font-semibold px-2.5 py-1 rounded-full"
                style={{ background: 'rgba(201,150,46,0.92)', color: '#08111F' }}
              >
                {study.industry || 'Technology'}
              </span>
              <span className="text-xs font-bold text-white/80">{study.brand}</span>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* KPI metric */}
            <div className="flex items-baseline gap-2 mb-4">
              <span
                className="text-4xl font-black leading-none"
                style={{
                  background: 'linear-gradient(135deg,#C9962E,#E8C25A)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {study.metric}
              </span>
              {(study.metricLabel || study.metric_label) && (
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">
                  {study.metricLabel || study.metric_label}
                </span>
              )}
            </div>

            {/* Title */}
            <h3 className="text-[#1F2D45] font-bold text-lg leading-snug mb-3">
              {study.title}
            </h3>

            {/* Summary */}
            <p className="text-slate-500 text-sm leading-relaxed mb-6">
              {study.summary}
            </p>

            {/* Actions */}
            <div className="flex items-center gap-4">
              <PrimaryButton
                icon={<ArrowRight className="h-4 w-4" />}
                onClick={handleViewMore}
              >
                View More
              </PrimaryButton>
              <button
                onClick={onClose}
                className="text-sm font-semibold text-slate-400 hover:text-slate-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
}
